import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button, Form, Input, Modal, notification } from 'tea-component';
import './index.scss';
import { FormMode } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import { editFollowingGroup, addFollowingGroup } from '@/pages/intelligence/services/following';
import { useIntl, getLocale } from 'umi';

const RenameAddGroup = (porps: any) => {
  const {
    visible,
    setCreateRenameGroup,
    mainSegment = 'mobile',
    mode,
    // groupNamePrefix = 'new group',
    groupName: groupNameProps,
    groupNames,
    onConfirm,
    openAddUpateGroup,
    setAddNewGroupName,
  } = porps;
  // console.log('RenameAddGroup', porps)
  const { formatMessage } = useIntl(); // 中英切换方法
  const locale = getLocale();
  const groupNamePrefix = useMemo(() => {
    return mode === FormMode.Add ? 'new group' : groupNameProps;
  }, [groupNameProps, mode]);

  const COMMON_CONFIG = {
    [FormMode.Add]: {
      title: formatMessage({ id: 'dbi_create_new_group' }),
      form: {
        groupName: {
          label: formatMessage({ id: 'dbi_new_group' }),
        },
      },
    },
    [FormMode.Edit]: {
      title: formatMessage({ id: 'dbi_rename_group' }),
      form: {
        groupName: {
          label: formatMessage({ id: 'dbi_group_name' }),
        },
      },
    },
  } as any;

  const DISABLED_CHARS = ['?', '？', '\\\\', '/', '|', '.'];

  const getNewGroupName = (prefix: string, groupNames: string[], isNew: boolean): string => {
    if (!Array.isArray(groupNames)) {
      console.warn('parameter groupNames invalid.');
      return '';
    }
    if (!groupNames.includes(prefix)) return prefix;
    let i = 0;
    let groupName = '';
    let isDuplicate = true;
    while (isDuplicate) {
      i++;
      groupName = isNew ? `${prefix} - copy ${i}` : `${groupNameProps} - ${i}`;
      isDuplicate = groupNames.includes(groupName);
      if (!isDuplicate) break;
    }
    return groupName;
  };

  const getInitialGroupName = (mode: FormMode, prefix: string, groupNames: string[]): string => {
    const isNew = mode === FormMode.Add;
    return getNewGroupName(prefix, groupNames, isNew);
  };

  const initialGroupName = getInitialGroupName(mode, groupNamePrefix, groupNames);

  const [isLoading, setIsLoading] = useState(false);
  const [isGroupNameValid, setIsGroupNameValid] = useState(true);
  const [groupNameValidMsg, setGroupNameValidMsg] = useState('');
  const [groupName, setGroupName] = useState(initialGroupName);

  const common = useMemo(() => {
    return COMMON_CONFIG[mode] || COMMON_CONFIG[FormMode.Add];
  }, [mode, locale]);

  const isFormValid = useMemo(() => {
    return groupName?.trim().length > 0 && isGroupNameValid;
  }, [groupName, isGroupNameValid]);

  const useDebounce = (fn: Function, delay = 300, dep: any[] = []): any => {
    if (!(fn instanceof Function)) return () => {};

    const { current } = useRef<any>({ fn, timer: null });

    useEffect(() => {
      current.fn = fn;
    }, [fn]);

    return useCallback((...args: any[]) => {
      if (current.timer) {
        clearTimeout(current.timer);
      }
      current.timer = setTimeout(() => {
        current.fn.call(this, ...args);
        current.timer = null;
      }, delay);
    }, dep);
  };

  const validateGroupName = (groupName: string): string => {
    const disabledCharsReg = new RegExp(`[${DISABLED_CHARS.join('')}]`, 'gi');
    let isDuplicate = false;
    if (disabledCharsReg.test(groupName))
      return `${formatMessage({ id: 'dbi_not_include_these_characters' })} ? \\ / | .`;
    if (groupName?.length > 30) return formatMessage({ id: 'dbi_maximum_characters' });
    if (Array.isArray(groupNames)) {
      isDuplicate = groupNames.includes(groupName);
    }
    if (isDuplicate) return formatMessage({ id: 'dbi_group_name_exists' });
    return '';
  };

  const handleGroupNameChange = (value: string) => {
    const msg = validateGroupName(value);
    setGroupName(value);
    setIsGroupNameValid(!msg);
    setGroupNameValidMsg(msg);
  };

  const handleAddSubmit = async (values: any): Promise<any> => {
    const { groupName } = values || {};
    const params = {
      entity_type: mainSegment,
      entity_name: groupName || '',
    };
    const res = await addFollowingGroup(params);
    return res && res.code === 0 ? Promise.resolve(res) : Promise.reject(res);
  };

  const handleEditSubmit = async (values: any): Promise<any> => {
    const { groupName: newGroupName } = values || {};
    const params = {
      entity_type: mainSegment,
      from_entity_names: groupNameProps,
      to_entity_names: newGroupName,
    };
    const res = await editFollowingGroup(params);
    return res && res.code === 0 ? Promise.resolve(res) : Promise.reject(res);
  };

  const handleSubmit = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setCreateRenameGroup && setCreateRenameGroup(false);
    try {
      const params = { groupName };
      if (mode === FormMode.Add) {
        await handleAddSubmit(params);
        setAddNewGroupName && setAddNewGroupName(groupName);
        openAddUpateGroup && openAddUpateGroup();
        notification.success({
          unique: true,
          description: formatMessage({ id: 'dbi_group_created_success' }),
        });
      } else {
        await handleEditSubmit(params);
      }
      if (onConfirm instanceof Function) {
        onConfirm();
      }
      // setVisible(false);
    } catch (exception) {
      // console.warn(exception);
      if (mode === FormMode.Add) {
        notification.error({
          unique: true,
          description: formatMessage({ id: 'dbi_group_created_success' }),
        });
      } else {
        notification.error({
          unique: true,
          description: formatMessage({ id: 'dbi_rename_failed_again' }),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitDebounce = useDebounce(handleSubmit);

  useEffect(() => {
    const initialGroupName = getInitialGroupName(mode, groupNamePrefix, groupNames);
    // console.log('initialGroupName', initialGroupName);
    setGroupName(initialGroupName);
    setIsGroupNameValid(true);
    setGroupNameValidMsg('');
  }, [visible, mode, groupNamePrefix, groupNames]);

  useEffect(() => {
    const inputEl = document.querySelector('.group-name-input');
    if (inputEl) {
      inputEl.setAttribute('autocomplete', 'off');
    }
  }, []);

  return (
    <Modal
      className="dbi-rename-group"
      visible={visible}
      size={500}
      onClose={() => setCreateRenameGroup && setCreateRenameGroup(false)}
      caption={common?.title}
    >
      <Modal.Body>
        <Form>
          <Form.Item
            showStatusIcon={false}
            // message="Maximum 30 Characters"
            className="dbi-rename-group-input"
            label={common?.form?.groupName?.label}
            status={isGroupNameValid ? 'success' : 'error'}
            message={isGroupNameValid ? '' : groupNameValidMsg}
          >
            <Input
              type="text"
              className="group-name-input"
              placeholder={formatMessage({ id: 'dbi_name_the_group' })}
              value={groupName}
              onChange={handleGroupNameChange}
              size="full"
            />
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          htmlType="submit"
          disabled={!isFormValid}
          loading={isLoading}
          onClick={handleSubmitDebounce}
        >
          {formatMessage({ id: 'dbi_confirm' })}
        </Button>
        <Button
          type="weak"
          onClick={() => {
            setCreateRenameGroup && setCreateRenameGroup(false);
          }}
        >
          {formatMessage({ id: 'dbi_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RenameAddGroup;
